/**
 * DocumentPreviewModal — opens a saved AI-chat document in a wide reading dialog.
 *
 * Background: saved chats are stored as `ai_documents` rows with `content` set
 * to the full chat transcript in Markdown (see `_save_thread_as_document` in
 * backend/routes/ai_hub.py). Until now the DocumentsHub UI had NO click handler
 * on document cards, so users couldn't actually open what they saved. This
 * dialog fixes that.
 *
 * Features:
 *  - Renders the markdown content with a tiny built-in renderer (we don't
 *    have react-markdown installed; the saved transcripts are well-structured
 *    so a regex pass handles 95%+ of cases cleanly).
 *  - "Download as PDF" via html2pdf.js (already in package.json).
 *  - "Open in AI Studio" deep-links to /ai-hub?thread=<source_thread_id>
 *    so the user can continue chatting where they left off.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { Loader2, Download, FileText, MessageSquare, Move, Edit3, Trash2, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../fetchInterceptor';
import MarkdownView from './MarkdownView';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tiny inner dialog used by Move + Rename actions. Kept inline so the preview
// modal stays the single mounted component on a card click.
function ActionDialog({ open, title, description, value, onChange, onSubmit, submitLabel, busy, children, onClose }) {
  if (!open) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="doc-action-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-2 py-2">{children}</div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy} data-testid="doc-action-submit">
            {busy ? 'Working…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DocumentPreviewModal({ documentId, onClose }) {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // Move dialog state
  const [movingOpen, setMovingOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');
  const [folders, setFolders] = useState([]);
  const [moveBusy, setMoveBusy] = useState(false);
  // Rename dialog state
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  // Inline file preview: iframes don't attach the Authorization header, so
  // Bearer-token sessions (no cookie) rendered {"detail":"Not authenticated"}.
  // Fetch the file with apiFetch (auth attached) and preview the blob URL.
  const [fileUrl, setFileUrl] = useState(null);
  const [fileError, setFileError] = useState(false);
  // "Report a problem" — files a support ticket into the Support Console
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportBusy, setReportBusy] = useState(false);

  const submitReport = async () => {
    if (!doc || reportText.trim().length < 10) {
      toast.error('Please describe the problem (at least 10 characters).');
      return;
    }
    setReportBusy(true);
    try {
      const r = await apiFetch(`${API_URL}/api/support/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: `Problem with document: ${(doc.title || doc.original_filename || 'Untitled')}`.slice(0, 200),
          description:
            `${reportText.trim()}\n\n— Auto-attached context —\n` +
            `Document: ${doc.title || 'Untitled'} (${doc.document_id})\n` +
            `File: ${doc.original_filename || '-'} · Type: ${doc.file_content_type || doc.upload_kind || 'saved chat'}`,
          category: 'bug_report',
          priority: 'medium',
          page_url: window.location.href.slice(0, 500),
          workspace_id: doc.workspace_id || null,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(typeof e.detail === 'string' ? e.detail : 'Could not send the report.');
        return;
      }
      const t = await r.json();
      toast.success(`Thanks! Our support team has been notified (${t.ticket_id || 'ticket created'}).`);
      setReportOpen(false);
      setReportText('');
    } catch {
      toast.error('Could not send the report.');
    } finally {
      setReportBusy(false);
    }
  };

  useEffect(() => {
    if (!doc) { setFileUrl(null); return undefined; }
    const isDirect = doc.upload_kind === 'direct' && (doc.file_content_type || '').match(/^(application\/pdf|image\/)/);
    const isScan = !isDirect && doc.upload_kind !== 'direct' && !!doc.source_ocr_job_id;
    if (!isDirect && !isScan) { setFileUrl(null); return undefined; }
    const src = isDirect
      ? `${API_URL}/api/ai-documents/${doc.document_id}/file`
      : `${API_URL}/api/ocr/${doc.source_ocr_job_id}/original`;
    let objectUrl = null;
    let cancelled = false;
    setFileUrl(null);
    setFileError(false);
    (async () => {
      try {
        const r = await apiFetch(src);
        if (!r.ok) throw new Error('fetch failed');
        const blob = await r.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setFileUrl(objectUrl);
      } catch {
        if (!cancelled) setFileError(true);
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [doc]);

  useEffect(() => {
    if (!documentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await apiFetch(`${API_URL}/api/ai-documents/${documentId}`);
        if (!r.ok) {
          if (!cancelled) toast.error('Could not load document.');
          return;
        }
        const d = await r.json();
        if (!cancelled) setDoc(d);
      } catch {
        if (!cancelled) toast.error('Could not load document.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [documentId]);

  const downloadPdf = async () => {
    if (!documentId || !doc) return;
    // Scanned-document path: the original PDF lives on the OCR job, not in
    // `content`. Fetch it as a blob and trigger a normal download. Without
    // this, the button was disabled (see the `disabled` prop below) because
    // `doc.content` is always empty for scans, and the /pdf endpoint would
    // have rendered a blank PDF from empty markdown.
    if (doc.upload_kind === 'direct') {
      setGenerating(true);
      try {
        const r = await apiFetch(`${API_URL}/api/ai-documents/${documentId}/file?download=1`);
        if (!r.ok) throw new Error('Original file not found');
        const blob = await r.blob();
        const dlBlob = new Blob([blob], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(dlBlob);
        const filename = doc.original_filename || `${(doc.title || 'document').replace(/[^a-z0-9_\- .]/gi, '').slice(0, 80)}`;
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          try { document.body.removeChild(a); } catch { /* */ }
          URL.revokeObjectURL(url);
        }, 30000);
        toast.success('File saved to Downloads');
      } catch (e) {
        toast.error(e?.message || 'Could not download the file.');
      } finally {
        setGenerating(false);
      }
      return;
    }

    if (doc.source_ocr_job_id) {
      setGenerating(true);
      try {
        const r = await apiFetch(
          `${API_URL}/api/ocr/${doc.source_ocr_job_id}/original?download=1`,
        );
        if (!r.ok) throw new Error('Original file not found');
        const blob = await r.blob();
        const dlBlob = new Blob([blob], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(dlBlob);
        const safeTitle = (doc?.title || doc?.original_filename || 'document')
          .replace(/[^a-z0-9_\- .]/gi, '').slice(0, 80) || 'document';
        const filename = doc.original_filename && doc.original_filename.includes('.')
          ? doc.original_filename
          : `${safeTitle}.pdf`;
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          try { document.body.removeChild(a); } catch { /* */ }
          URL.revokeObjectURL(url);
        }, 30000);
        toast.success('File saved to Downloads');
      } catch (e) {
        toast.error(e?.message || 'Could not download the original file.');
      } finally {
        setGenerating(false);
      }
      return;
    }

    setGenerating(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-documents/${documentId}/pdf`);
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'PDF render failed');
      }
      const blob = await r.blob();
      // Force a real-file MIME so the browser doesn't render PDF inline.
      const dlBlob = new Blob([blob], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(dlBlob);
      const safeTitle = (doc?.title || 'document').replace(/[^a-z0-9_\- ]/gi, '').slice(0, 80) || 'document';
      const filename = `${safeTitle}.pdf`;

      // Programmatic anchor click — works in normal browsers AND in iframe
      // sandboxes that include the `allow-downloads` permission. Using
      // application/octet-stream + the `download` attribute together is the
      // most reliable way to force a real download instead of opening the
      // PDF inline (which is what was failing for the user).
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      // Hold the URL briefly so the browser has time to consume the click;
      // GC the blob after ~30s so we don't leak memory.
      setTimeout(() => {
        try { document.body.removeChild(a); } catch { /* */ }
        URL.revokeObjectURL(url);
      }, 30000);

      toast.success('PDF saved to Downloads', {
        description: `Look for ${filename} in your browser's download folder.`,
      });
    } catch (e) {
      toast.error(e?.message || 'Could not generate PDF.');
      console.error(e);
    } finally {
      setGenerating(false);
    }
  };

  const openInAIStudio = () => {
    if (!doc?.source_thread_id) {
      toast.info('No source chat thread on this document.');
      return;
    }
    navigate(`/ai-hub?thread=${doc.source_thread_id}`);
    onClose?.();
  };

  // Load destination-folder list when the Move dialog is opened. Pulls every
  // folder in the document's workspace so the user can pick anywhere.
  const openMoveDialog = async () => {
    setMoveTarget(doc?.folder_id || '');
    setMovingOpen(true);
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${doc.workspace_id}/folders`);
      if (r.ok) {
        const data = await r.json();
        // The backend returns a bare array; older callers expected
        // `{folders: [...]}` — accept both shapes so refactors don't break
        // the move UX again.
        const list = Array.isArray(data) ? data : (data.folders || []);
        setFolders(list);
      }
    } catch { /* silent */ }
  };

  const submitMove = async () => {
    if (!doc) return;
    setMoveBusy(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-documents/${doc.document_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder_id: moveTarget || null }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Move failed');
        return;
      }
      const updated = await r.json();
      const dest = folders.find(f => f.folder_id === moveTarget);
      toast.success(`Moved to ${dest?.name || 'unfiled'}`);
      setDoc(updated);
      setMovingOpen(false);
    } finally {
      setMoveBusy(false);
    }
  };

  const openRenameDialog = () => {
    setRenameValue(doc?.title || '');
    setRenameOpen(true);
  };

  const submitRename = async () => {
    if (!doc || !renameValue.trim()) return;
    setRenameBusy(true);
    try {
      const r = await apiFetch(`${API_URL}/api/ai-documents/${doc.document_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: renameValue.trim() }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Rename failed');
        return;
      }
      const updated = await r.json();
      toast.success('Renamed');
      setDoc(updated);
      setRenameOpen(false);
    } finally {
      setRenameBusy(false);
    }
  };

  return (
    <Dialog open={!!documentId} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-[1100px] w-[95vw] h-[90vh] max-h-[90vh] p-0 flex flex-col overflow-hidden"
        data-testid="document-preview-modal"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-lg" data-testid="document-preview-title">
            <FileText className="w-5 h-5 text-emerald-500" />
            {loading ? 'Loading…' : (doc?.title || 'Untitled')}
          </DialogTitle>
          <DialogDescription>
            {doc?.message_count ? `${doc.message_count} messages · ` : ''}
            {doc?.updated_at ? `Last updated ${new Date(doc.updated_at).toLocaleString()}` : ''}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="document-preview-loading">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading document…
            </div>
          ) : doc?.upload_kind === 'direct' ? (
            (doc.file_content_type || '').match(/^(application\/pdf|image\/)/) ? (
              <div className="w-full h-full min-h-[70vh] bg-muted/40" data-testid="document-preview-file">
                {fileError ? (
                  <div className="flex flex-col items-center justify-center py-20 gap-3 text-center px-8">
                    <FileText className="w-10 h-10 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Couldn't load the preview — use the Download button below.</p>
                  </div>
                ) : !fileUrl ? (
                  <div className="flex items-center justify-center py-20 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading preview…
                  </div>
                ) : (
                  <iframe
                    title={doc?.title || 'Uploaded file'}
                    src={fileUrl}
                    className="w-full h-full min-h-[70vh] border-0"
                  />
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center px-8" data-testid="document-preview-nopreview">
                <FileText className="w-10 h-10 text-muted-foreground" />
                <p className="text-sm font-medium">{doc.original_filename || doc.title}</p>
                <p className="text-xs text-muted-foreground">
                  This file type can't be previewed in the browser — use the Download button below.
                </p>
              </div>
            )
          ) : doc?.source_ocr_job_id ? (
            // Scanned-document preview: the original PDF is fetched with auth
            // and rendered from a blob URL (iframes can't send Bearer tokens).
            <div className="w-full h-full min-h-[70vh] bg-muted/40" data-testid="document-preview-pdf">
              {fileError ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3 text-center px-8">
                  <FileText className="w-10 h-10 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Couldn't load the preview — use the Download button below.</p>
                </div>
              ) : !fileUrl ? (
                <div className="flex items-center justify-center py-20 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading preview…
                </div>
              ) : (
                <iframe
                  title={doc?.title || 'Scanned document'}
                  src={fileUrl}
                  className="w-full h-full min-h-[70vh] border-0"
                />
              )}
            </div>
          ) : (
            <div
              className="px-8 py-6"
              data-testid="document-preview-content"
            >
              <MarkdownView>{doc?.content || ''}</MarkdownView>
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="px-6 py-3 border-t bg-background flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={openRenameDialog}
            className="gap-1.5"
            data-testid="document-preview-rename"
            disabled={loading || !doc}
          >
            <Edit3 className="w-3.5 h-3.5" /> Rename
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openMoveDialog}
            className="gap-1.5"
            data-testid="document-preview-move"
            disabled={loading || !doc}
          >
            <Move className="w-3.5 h-3.5" /> Move
          </Button>
          {doc?.source_thread_id && (
            <Button
              variant="outline"
              size="sm"
              onClick={openInAIStudio}
              className="gap-1.5"
              data-testid="document-preview-open-chat"
            >
              <MessageSquare className="w-3.5 h-3.5" /> Open in AI Studio
            </Button>
          )}
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setReportOpen(true)}
            disabled={loading || !doc}
            className="gap-1.5 text-muted-foreground mr-auto"
            data-testid="document-preview-report-problem"
          >
            <LifeBuoy className="w-3.5 h-3.5" /> Report a problem
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            data-testid="document-preview-close"
          >
            Close
          </Button>
          <Button
            size="sm"
            onClick={downloadPdf}
            disabled={
              generating
              || loading
              || (!doc?.content && !doc?.source_ocr_job_id && doc?.upload_kind !== 'direct')
            }
            className="gap-1.5 bg-emerald-600 hover:bg-emerald-500"
            data-testid="document-preview-download"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {(doc?.source_ocr_job_id || doc?.upload_kind === 'direct') ? 'Download original' : 'Download PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Rename + Move sub-dialogs — replace window.prompt which is blocked in
        * iframe sandboxes. */}
      <ActionDialog
        open={renameOpen}
        title="Rename document"
        description="Pick a new name for this saved chat."
        submitLabel="Rename"
        busy={renameBusy}
        onSubmit={submitRename}
        onClose={() => setRenameOpen(false)}
      >
        <Input
          autoFocus
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          placeholder="Document title"
          onKeyDown={(e) => { if (e.key === 'Enter') submitRename(); }}
          data-testid="document-rename-input"
        />
      </ActionDialog>
      <ActionDialog
        open={movingOpen}
        title="Move document"
        description="Pick a destination folder. Leave as 'Unfiled' to remove it from any folder."
        submitLabel="Move here"
        busy={moveBusy}
        onSubmit={submitMove}
        onClose={() => setMovingOpen(false)}
      >
        <select
          value={moveTarget}
          onChange={(e) => setMoveTarget(e.target.value)}
          className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
          autoFocus
          data-testid="document-move-select"
        >
          <option value="">— Unfiled —</option>
          {folders.map((f) => (
            <option key={f.folder_id} value={f.folder_id}>{f.name}</option>
          ))}
        </select>
      </ActionDialog>
      <ActionDialog
        open={reportOpen}
        title="Report a problem"
        description="Tell us what went wrong with this document — our support team will take a look. The document details are attached automatically."
        submitLabel="Send report"
        busy={reportBusy}
        onSubmit={submitReport}
        onClose={() => setReportOpen(false)}
      >
        <textarea
          autoFocus
          value={reportText}
          onChange={(e) => setReportText(e.target.value)}
          placeholder="e.g. The preview won't load / the file looks wrong / this was filed in the wrong folder…"
          rows={4}
          className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="document-report-textarea"
        />
      </ActionDialog>
    </Dialog>
  );
}
